/**
 * Spec 004.5 Viewer Dashboard — EngineAdapter TypeScript surface
 * (MAD-1196 / T003).
 *
 * Mirrors `specs/004.5-viewer-dashboard/contracts/engine-adapter-
 * interface.md` exactly. The wasm bundle from
 * `crates/slot-engine-wgpu/pkg/` ships four exports (`run_webgpu`,
 * `run_cpu`, `webgpu_probe`, `cancel_run`); the adapter classes in
 * `cpu.ts` + `webgpu.ts` wrap those exports under the unified
 * `EngineAdapter` interface so the result panels stay backend-
 * agnostic.
 */

// ──────────────────────────────────────────────────────────────────
// Core handles
// ──────────────────────────────────────────────────────────────────

/**
 * Opaque identifier for an in-progress simulation. Returned by
 * `EngineAdapter.run()` and accepted by `EngineAdapter.cancel()`.
 *
 * An adapter MAY reuse RunIds across runs; callers MUST NOT store
 * a RunId after the corresponding `cancel()` call or after the
 * `run()` Promise settles (resolve or reject).
 */
export type RunId = string;

/**
 * Progress event callback. Fired at chunk-completion boundaries on
 * the WebGPU path (spec-006.5 FR-018) or once at start on the
 * CPU-WASM path (indeterminate).
 *
 * @param done    - Spins confirmed complete so far.
 * @param total   - Total spins requested. `null` when the adapter
 *                  cannot report fine-grained progress (CPU-WASM
 *                  "indeterminate" shape).
 * @param partial - Streaming headline metrics for the spins completed
 *                  so far, on the WebGPU GPU-shader-aggregator path.
 *                  `null`/absent on count-only ticks and on engines that
 *                  don't stream intermediate metrics (CPU-WASM, raw
 *                  outcome path). The values converge to the final
 *                  `MetricsResult` as `done → total`.
 */
export type ProgressCallback = (
  done: number,
  total: number | null,
  partial?: MetricsResult | null,
) => void;

// ──────────────────────────────────────────────────────────────────
// Simulation request / response shapes
// ──────────────────────────────────────────────────────────────────

/**
 * Input parameters for `EngineAdapter.run()`.
 *
 * `nSpins` is clamped to `[10_000, 1_000_000]` by the UI layer
 * (FR-003) before reaching the adapter. The adapter does not
 * re-validate the bound — callers MUST respect it.
 *
 * `seed` is a 16-character lowercase hex string encoding a u64.
 * Generated via `crypto.getRandomValues()` unless the user
 * supplied an explicit value. Both adapters pass it verbatim to
 * the WASM module.
 *
 * `planJson` / `configHash` are the two extra inputs the
 * GPU-shader-aggregator path (`run_webgpu_metrics`, spec 006.6 T043)
 * needs beyond the IR: the Rust wire-format `ExecutionPlan` JSON and
 * the canonical GameAST config hash. They are raw strings passed
 * verbatim to WASM (serde re-parses the plan; the hash is tagged into
 * the returned `MetricsResult`). Present only when the loaded IR is
 * one the UI has matching sibling fixtures for (the default Tomb
 * Explorer); absent (an uploaded custom IR) routes the `webgpu`
 * adapter back to the raw-outcome `run_webgpu` + CPU-fold path.
 * Ignored by the CPU adapter.
 *
 * `aggregatorLayout` (MAD-1983) is a perf-investigation knob sourced
 * from the `?aggregator=` URL query param: `"packed"` / `"unpacked"`
 * force the GPU aggregator storage-buffer layout for A/B benchmarking,
 * `"auto"` (or absent) keeps the adapter-limit-driven default. Consulted
 * only on the `webgpu` adapter's GPU-shader-aggregator path; ignored by
 * the CPU adapter and on the raw-outcome path.
 */
export type AggregatorLayoutOverride = "packed" | "unpacked" | "auto";

export interface SimulationRequest {
  config: Record<string, unknown>;
  nSpins: number;
  seed: string;
  planJson?: string;
  configHash?: string;
  aggregatorLayout?: AggregatorLayoutOverride;
}

/**
 * RTP point estimate + 95% CI bounds, both for two-tailed CI and
 * for the half-width used in the headline panel's ± display.
 */
export interface RtpStat {
  point: number;
  ci_lo_95: number;
  ci_hi_95: number;
  ci_half_width: number;
}

/**
 * Volatility statistic — per-spin payout standard deviation +
 * a categorical bucket the headline panel renders verbatim.
 *
 * `stdev_noncoinciding` is the spec-003.7 / 003 stdev excluding
 * coinciding wins per FR-008.
 */
export interface VolatilityStat {
  stdev: number;
  class_: "low" | "medium" | "high" | "very-high";
  stdev_noncoinciding: number;
}

/**
 * Win-distribution histogram. Exactly 15 log-scaled buckets per
 * the canonical schema; the histogram panel renders all 15 as a
 * stacked bar.
 */
export interface Histogram {
  buckets: HistogramBucket[];
  scale: "log";
}

export interface HistogramBucket {
  lo: number;
  hi: number;
  count: number;
}

/** Bonus-game contribution as both an absolute RTP and a fraction
 * of total RTP (the bonus & triggers panel renders both).
 */
export interface BonusContribution {
  /** Total RTP (fraction, e.g. 0.42 == 42 RTP points) contributed by all
   *  bonus/feature states combined. Canonical `MetricsResult` v1.3 shape. */
  total: number;
  /** Per-feature breakdown of `total` (fraction each), keyed by feature id
   *  (e.g. `free_spins`, `pick_bonus`). */
  per_feature: Record<string, number>;
}

/**
 * Per-feature trigger stats. `count` is a u64 — for very large
 * runs this may exceed JS's safe-integer range (2^53); the WASM
 * module emits as a JSON number nevertheless and the panel
 * displays the value as-is (UI doesn't math against `count`
 * directly, only displays it).
 */
export interface FeatureTriggerStats {
  count: number;
  rate: number;
}

/** Observed value range for a constraint or metric. */
export interface ObservedBound {
  min: number;
  max: number;
}

/**
 * Aggregated simulation result. Produced by either backend with
 * byte-identical content for the same `(config, seed, nSpins)`
 * tuple (Constitution Principle II / FR-022 / SC-009).
 *
 * Field-by-field mapping to result panels is documented in
 * `specs/004.5-viewer-dashboard/data-model.md` §Panel mapping.
 */
export interface MetricsResult {
  rtp: RtpStat;
  volatility: VolatilityStat;
  hit_frequency: number;
  histogram: Histogram;
  max_win_observed: number;
  max_win_bound: number | null;
  bonus_contribution: BonusContribution;
  per_symbol_contribution: Record<string, number>;
  rtp_per_rule: Record<string, number>;
  hits_per_rule: Record<string, number>;
  feature_triggers: Record<string, FeatureTriggerStats>;
  /**
   * Spec 013 — per-feature award win-distribution. Keyed by feature id
   * (e.g. `pick_bonus`); each value is the standard 15-bucket log
   * `Histogram` of that feature's award amounts (one sample per award
   * event, pre-`max_win`-cap). Only features with ≥1 award event appear;
   * an awardless run is `{}`. Optional because pre-1.5 `MetricsResult`s
   * (and engines that don't emit it) omit the field entirely.
   */
  per_feature_award_histogram?: Record<string, Histogram>;
  observed_bounds: Record<string, ObservedBound>;
  insufficient_sample_flags: string[];
  schema_version: "1.2" | "1.3" | "1.4" | "1.5";
  n_spins: number;
  config_hash: string;
}

// ──────────────────────────────────────────────────────────────────
// Replayed per-spin outcomes (spec 018 US5 / T042)
// ──────────────────────────────────────────────────────────────────

/**
 * The `action_kind` discriminant of a {@link StatePathStep}. An OPEN
 * union: the known kinds are enumerated for editor autocomplete and
 * exhaustiveness hints, but the trailing `(string & {})` keeps the
 * type permissive so a kind a newer WASM bundle emits (or one this
 * union hasn't caught up to) still parses without a cast.
 *
 * Spec 019 US4 (T033 / FR-020) adds the two cascade ops —
 * `remove_winning_symbols` and `drop_symbols` — alongside the per-
 * evaluate grids/multipliers the cascade replay already rides
 * (spec 006.3 `evaluate` detail). The cascade introduces no new
 * engine→viewer contract field; widening this union is the whole
 * type-surface change the viewer needs to read a tumble.
 */
export type ActionKind =
  | "spin"
  | "spin_respecting_locks"
  | "evaluate"
  | "trigger"
  | "set"
  | "decrement"
  | "loop_iter"
  | "on_exit"
  | "lock_position"
  | "shift_positions"
  | "expand_to_reel"
  | "award"
  // Spec 019 US4 — cascading-reels ops (carry their own state_path steps;
  // `remove_winning_symbols` → `{removed: [[reel,row], …]}`,
  // `drop_symbols` → `{reels: <reel-set-id>}`).
  | "remove_winning_symbols"
  | "drop_symbols"
  // Open-union escape hatch: any other kind a future bundle emits.
  | (string & {});

/**
 * One step of a spin's state-machine execution path. Mirrors
 * `slot-engine-core::types::StatePathStep` as serialised by the WASM
 * bundle's raw-outcome path (`run_cpu` / `run_webgpu`).
 *
 * `detail` is per-`action_kind` JSON: `evaluate` carries
 * `{grid, win, multiplier, line_count}` (spec 006.3 per-evaluate
 * grid), the spec-018 wild ops carry their replay payloads —
 * `spin_respecting_locks` → `{locks, locked_mask}` (the pre-stamp
 * mask, FR-016), `lock_position` → `{collection, added, size}`,
 * `shift_positions` → `{collection, d_reel, d_row, size}`,
 * `expand_to_reel` → `{reel, symbol}` — and the spec-019 cascade ops
 * carry `remove_winning_symbols` → `{removed: [[reel,row], …]}` (the
 * cells the immediately-preceding `evaluate`'s winning mask vacated)
 * and `drop_symbols` → `{reels: <reel-set-id>}`.
 */
export interface StatePathStep {
  state: string;
  action_index: number;
  action_kind: ActionKind;
  detail?: Record<string, unknown> | null;
}

/**
 * One spin's full replayable result — mirrors
 * `slot-engine-core::types::SpinOutcome` (serde JSON via the wasm
 * pkg's raw-outcome path).
 *
 * `locked_mask` (spec 018 FR-016 / R3-a) is the END-of-spin
 * locked-position state as a packed bitmask — bit `reel*3 + row`
 * over the fixed 5x3 grid. Optional because pre-018 wasm bundles
 * omit the field; treat absence as 0. The per-respin locked state
 * the replay view renders rides `state_path` detail (the
 * `spin_respecting_locks` steps), not this end-of-spin summary.
 *
 * Note: spec 018 does NOT bump `MetricsResult.schema_version` (the
 * audit-log schema bumps 1.4 → 1.5 instead), so the union above
 * stays at `"1.5"`.
 */
export interface ReplayedOutcome {
  winnings: number;
  grid: string[][];
  state_path: StatePathStep[];
  features_fired: string[];
  rng_draws_consumed: number;
  locked_mask?: number;
}

/**
 * Decode the wasm raw-outcome envelope (UTF-8 JSON bytes of a
 * `SpinOutcome[]`) into `ReplayedOutcome[]`. Shared by the adapters'
 * `sampleWinnings` / `sampleOutcomes` paths.
 */
export function decodeReplayedOutcomes(bytes: Uint8Array): ReplayedOutcome[] {
  const decoder = new TextDecoder("utf-8");
  const json = decoder.decode(bytes);
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch (err) {
    const detail = err instanceof Error ? err.message : String(err);
    throw new EngineError(
      `Raw-outcome envelope is not valid JSON: ${detail}`,
      "ENGINE_ERROR",
      detail,
    );
  }
  if (!Array.isArray(parsed)) {
    throw new EngineError(
      "Raw-outcome envelope is not an array",
      "ENGINE_ERROR",
    );
  }
  return parsed as ReplayedOutcome[];
}

// ──────────────────────────────────────────────────────────────────
// Adapter interface + concrete-class brands
// ──────────────────────────────────────────────────────────────────

/**
 * The simulation engine abstraction. Two concrete implementations:
 * - `WasmWebGpuEngineAdapter` — wraps `pkg/`'s `run_webgpu` export.
 * - `WasmCpuEngineAdapter` — wraps `pkg/`'s `run_cpu` export.
 *
 * Construction goes through `createEngineAdapter()`; do not
 * `new` either concrete class directly outside the engine module.
 *
 * The interface is the only seam between the simulation
 * infrastructure and the display layer (FR-004/005/006). Panel
 * components MUST NOT import the concrete classes; verification:
 *
 *   grep -r "WasmWebGpuEngineAdapter\|WasmCpuEngineAdapter" \\
 *       ui/src/components/panels/
 *
 * MUST return zero matches.
 */
export interface EngineAdapter {
  /** Active backend; stable for an adapter instance's lifetime. */
  readonly mode: "webgpu" | "cpu";

  /**
   * Run a simulation.
   *
   * Returns a `RunId` immediately; the simulation executes
   * asynchronously. The returned Promise resolves to a
   * `MetricsResult` on success or rejects with an `EngineError`
   * subclass on failure.
   *
   * `onProgress` semantics:
   * - WebGPU path: fired per folded chunk per spec-006.5 FR-018.
   *   `total` carries `nSpins`. For single-dispatch runs (typical
   *   `nSpins ≤ compute_chunk_size`, ~3-26M depending on device),
   *   fires exactly once at completion.
   * - CPU-WASM path: fired twice — `(0, null)` at start, then
   *   `(nSpins, null)` at end. Indeterminate ("null total")
   *   matches the contract's CPU shape; the UI's ProgressBar
   *   renders an indeterminate spinner.
   *
   * Concurrent `run()` calls on the same adapter are permitted
   * and execute in parallel. Each RunId is independent.
   */
  run(
    request: SimulationRequest,
    onProgress?: ProgressCallback,
  ): [RunId, Promise<MetricsResult>];

  /**
   * Cancel an in-progress simulation. The run's Promise rejects
   * with `CancelledError`. In-flight chunk results are discarded;
   * no partial `MetricsResult` is emitted.
   *
   * `cancel()` on an unknown or already-settled RunId is a no-op.
   */
  cancel(runId: RunId): void;

  /**
   * Run `request.nSpins` spins and resolve to the per-spin winnings
   * array (stake-multiples — `RTP == mean(winnings)`), via the
   * raw-outcome path.
   *
   * Intended for SMALL `nSpins` (≤ ~100k), where materialising every
   * outcome in JS heap is cheap. It is the per-spin counterpart to
   * `run()`, which only yields an aggregated `MetricsResult` (and, for
   * large N, runs the on-device aggregator that never materialises
   * per-spin data). Used for fine-grained client-side series such as
   * the RTP-over-spins convergence chart.
   *
   * Deterministic in `(config, seed)`: counter-based Philox makes spin
   * `k` bit-identical regardless of the total spin count, so the first
   * `k` winnings of a sample pass equal the first `k` of any larger run
   * on the same seed.
   */
  sampleWinnings(request: SimulationRequest): Promise<number[]>;

  /**
   * Run `request.nSpins` spins and resolve to the full per-spin
   * `ReplayedOutcome[]` (grids + state_path + locked-position state),
   * for the spin-replay view (spec 018 US5).
   *
   * Intended for SMALL `nSpins` (≤ ~1k). Both local adapters route
   * this through the CPU-WASM raw path (`run_cpu`) regardless of
   * `mode` — the CPU engines emit the full state-machine `state_path`
   * with the wild-mechanic replay detail, while the GPU raw path
   * synthesises a minimal `state_path` without it. Deterministic in
   * `(config, seed)` like `sampleWinnings` (counter-based Philox).
   */
  sampleOutcomes(request: SimulationRequest): Promise<ReplayedOutcome[]>;
}

// ──────────────────────────────────────────────────────────────────
// Error hierarchy
// ──────────────────────────────────────────────────────────────────

/**
 * Common base for every `run()`-rejection error class. The `code`
 * field follows the CLAUDE.md §5.3 stable-string-code convention;
 * the UI routes states based on `code`, not `instanceof` checks,
 * so a future refactor that splits a class can preserve UX
 * routing.
 */
export class EngineError extends Error {
  readonly code: string;

  /** Optional detail string for the error panel's expanded view. */
  readonly detail: string | undefined;

  constructor(message: string, code = "ENGINE_ERROR", detail?: string) {
    super(message);
    this.name = "EngineError";
    this.code = code;
    this.detail = detail;
  }
}

/**
 * The game config failed spec-001 schema validation. `detail`
 * carries the first validation error message; the UI surfaces it
 * inline below the file-upload zone, naming the failing field.
 */
export class ValidationError extends EngineError {
  constructor(detail: string) {
    super(`Validation failed: ${detail}`, "ENGINE_VALIDATION_FAILED", detail);
    this.name = "ValidationError";
  }
}

/**
 * `cancel(runId)` was called before the run completed. Not an
 * error from the user's perspective — the UI clears progress
 * state without displaying an error panel.
 */
export class CancelledError extends EngineError {
  constructor() {
    super("Simulation cancelled", "ENGINE_CANCELLED");
    this.name = "CancelledError";
  }
}

/**
 * Neither backend could be initialised. Thrown by
 * `createEngineAdapter()` — never by `run()`. The UI shows an
 * error panel with "Simulation engine not available — WebAssembly
 * required." and disables the Run button.
 */
export class WasmUnavailableError extends EngineError {
  constructor(detail = "WebAssembly engine could not be initialised") {
    super(detail, "ENGINE_WASM_UNAVAILABLE", detail);
    this.name = "WasmUnavailableError";
  }
}

/**
 * `crossOriginIsolated === false` when the CPU backend is
 * required. Thrown by `createEngineAdapter()` in `cpu` mode, or
 * in `auto` mode when WebGPU is ALSO unavailable.
 *
 * The UI surfaces a non-blocking warning banner: "CPU backend
 * requires cross-origin isolation. See deployment notes." WebGPU
 * backend remains usable when it's available.
 */
export class CrossOriginIsolationError extends EngineError {
  constructor() {
    super(
      "CPU backend requires cross-origin isolation (COOP/COEP headers)",
      "ENGINE_CROSS_ORIGIN_ISOLATION_REQUIRED",
    );
    this.name = "CrossOriginIsolationError";
  }
}
